import React, {
  useMemo,
  useState,
  useCallback,
  memo,
} from "react";

import "./Portfolio.css";

/* =========================================
   IMAGE PATH HELPER
========================================= */
const getImagePath = (path) => {
  return path
    .replace(".JPG", ".webp")
    .replace(".jpg", ".webp")
    .replace(".JPEG", ".webp")
    .replace(".jpeg", ".webp")
    .replace(".PNG", ".webp")
    .replace(".png", ".webp");
};

/* =========================================
   CARD COMPONENT
========================================= */
const PortfolioCard = memo(({ project }) => {
  return (
    <div className="comparison-card">
      {/* BEFORE IMAGE CONTAINER */}
      <div className="image-wrapper">
        <img
          src={project.before}
          alt={`${project.subCategory} Before`}
          className="portfolio-img"
          loading="lazy"
          decoding="async"
          draggable="false"
          onError={(e) => {
            if (e.target.src.includes(".webp")) {
              e.target.src = e.target.src.replace(".webp", ".jpg");
            }
          }}
        />
        <div className="image-label before">BEFORE</div>
      </div>

      {/* AFTER IMAGE CONTAINER */}
      <div className="image-wrapper">
        <img
          src={project.after}
          alt={`${project.subCategory} After`}
          className="portfolio-img"
          loading="lazy"
          decoding="async"
          draggable="false"
          onError={(e) => {
            if (e.target.src.includes(".webp")) {
              e.target.src = e.target.src.replace(".webp", ".jpg");
            }
          }}
        />
        <div className="image-label after">AFTER</div>
      </div>
    </div>
  );
});

/* =========================================
   MAIN COMPONENT
========================================= */
function Portfolio() {
  /* =========================================
     MAIN TOPICS (Default Active State)
  ========================================= */
  const [mainFilter, setMainFilter] = useState("Real Estate");

  /* =========================================
     SUB TOPICS STATE
  ========================================= */
  const [realEstateFilter, setRealEstateFilter] = useState("");

  /* =========================================
     PROJECTS DATA ARRAY (Fixed All Duplicate IDs)
  ========================================= */
  const projects = useMemo(() => [
    /* =========================================
       REAL ESTATE - INTERIOR
    ========================================= */
    {
      id: 1,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/1.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/1.jpg"),
    },
    {
      id: 2,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/2.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/2.jpg"),
    },
    {
      id: 3,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/3.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/3.jpg"),
    },
    {
      id: 4,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/4.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/4.jpg"),
    },
    {
      id: 5,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/5.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/5.jpg"),
    },
    {
      id: 6,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/6.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/6.jpg"),
    },
    {
      id: 7,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/7.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/7.jpg"),
    },
    {
      id: 8,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/8.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/8.jpg"),
    },
    {
      id: 9,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/9.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/9.jpg"),
    },
    {
      id: 10,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/10.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/10.jpg"),
    },
    {
      id: 11,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/11.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/11.jpg"),
    },
    {
      id: 12,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/12.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/12.jpg"),
    },
    {
      id: 13,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/13.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/13.jpg"),
    },
    {
      id: 14,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/14.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/14.jpg"),
    },
    {
      id: 15,
      category: "Real Estate",
      subCategory: "Interior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/Before/15.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Interior/After/15.jpg"),
    },
    /* =========================================
       REAL ESTATE - EXTERIOR
    ========================================= */
    {
      id: 16,
      category: "Real Estate",
      subCategory: "Exterior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/Before/1.JPG"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/After/1.JPG"),
    },
    {
      id: 17,
      category: "Real Estate",
      subCategory: "Exterior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/Before/2.JPG"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/After/2.JPG"),
    },
    {
      id: 18,
      category: "Real Estate",
      subCategory: "Exterior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/Before/3.JPG"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/After/3.JPG"),
    },
    {
      id: 19,
      category: "Real Estate",
      subCategory: "Exterior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/Before/4.JPG"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/After/4.JPG"),
    },
    {
      id: 20,
      category: "Real Estate",
      subCategory: "Exterior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/Before/5.JPG"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/After/5.JPG"),
    },
    {
      id: 21,
      category: "Real Estate",
      subCategory: "Exterior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/Before/6.JPG"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/After/6.JPG"),
    },
    {
      id: 22,
      category: "Real Estate",
      subCategory: "Exterior",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/Before/7.JPG"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Exterior/After/7.JPG"),
    },
    /* =========================================
       REAL ESTATE - DRONE
    ========================================= */
    {
      id: 23,
      category: "Real Estate",
      subCategory: "Drone",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Drone/Before/1.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Drone/After/1.jpg"),
    },
    {
      id: 24,
      category: "Real Estate",
      subCategory: "Drone",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Drone/Before/2.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Drone/After/2.jpg"),
    },
    {
      id: 25,
      category: "Real Estate",
      subCategory: "Drone",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Drone/Before/3.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Drone/After/3.jpg"),
    },
    {
      id: 26,
      category: "Real Estate",
      subCategory: "Drone",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Drone/Before/4.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Drone/After/4.jpg"),
    },
    {
      id: 27,
      category: "Real Estate",
      subCategory: "Drone",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Drone/Before/5.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Drone/After/5.jpg"),
    },
    /* =========================================
       REAL ESTATE - VIRTUAL RENOVATION
    ========================================= */
    {
      id: 28,
      category: "Real Estate",
      subCategory: "Virtual Renovation",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Renovation/Before/1.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Renovation/After/1.jpg"),
    },
    {
      id: 29,
      category: "Real Estate",
      subCategory: "Virtual Renovation",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Renovation/Before/2.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Renovation/After/2.jpg"),
    },
    {
      id: 30,
      category: "Real Estate",
      subCategory: "Virtual Renovation",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Renovation/Before/3.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Renovation/After/3.jpg"),
    },
    {
      id: 31,
      category: "Real Estate",
      subCategory: "Virtual Renovation",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Renovation/Before/4.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Renovation/After/4.jpg"),
    },
    {
      id: 32,
      category: "Real Estate",
      subCategory: "Virtual Renovation",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Renovation/Before/5.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Renovation/After/5.jpg"),
    },
    {
      id: 33,
      category: "Real Estate",
      subCategory: "Virtual Renovation",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Renovation/Before/6.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Renovation/After/6.jpg"),
    },
    /* =========================================
       REAL ESTATE - VIRTUAL STAGING
    ========================================= */
    {
      id: 34,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/1.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/1.jpg"),
    },
    {
      id: 35,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/2.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/2.jpg"),
    },
    {
      id: 36,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/3.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/3.jpg"),
    },
    {
      id: 37,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/4.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/4.jpg"),
    },
    {
      id: 38,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/5.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/5.jpg"),
    },
    {
      id: 39,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/6.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/6.jpg"),
    },
    {
      id: 40,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/7.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/7.jpg"),
    },
    {
      id: 41,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/8.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/8.jpg"),
    },
    {
      id: 42,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/9.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/9.jpg"),
    },
    {
      id:43,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/10.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/10.jpg"),
    },
    {
      id: 44,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/11.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/11.jpg"),
    },
    {
      id: 45,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/12.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/12.jpg"),
    },
    {
      id: 46,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/13.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/13.jpg"),
    },
    {
      id: 47,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/14.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/14.jpg"),
    },
    {
      id: 48,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/15.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/15.jpg"),
    },
    {
      id: 49,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/16.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/16.jpg"),
    },
    {
      id: 50,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/17.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/17.jpg"),
    },
    {
      id: 51,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/18.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/18.jpg"),
    },
    {
      id: 52,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/19.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/19.jpg"),
    },
    {
      id: 53,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/20.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/20.jpg"),
    },
    {
      id: 54,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/21.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/21.jpg"),
    },
    {
      id: 55,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/22.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/22.jpg"),
    },
    {
      id: 56,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/23.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/23.jpg"),
    },
    {
      id: 57,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/24.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/24.jpg"),
    },
    {
      id: 58,
      category: "Real Estate",
      subCategory: "Virtual Staging",
      before: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/Before/25.jpg"),
      after: getImagePath("/Portfolio Photos/Real Estate Editing/Virtual Staging/After/25.jpg"),
    }
  ], []);

  /* =========================================
     FILTER EXTRACTIONS LOGIC
  ========================================= */
  const filteredProjects = useMemo(() => {
    if (mainFilter === "Real Estate") {
      // Subtopic select pannalana images return aagadhu (Blank Area display)
      if (!realEstateFilter) {
        return [];
      }
      return projects.filter(
        (p) =>
          p.category === "Real Estate" && p.subCategory === realEstateFilter
      );
    }
    return projects.filter((p) => p.category === mainFilter);
  }, [mainFilter, realEstateFilter, projects]);

  /* =========================================
     CALLBACK HANDLERS
  ========================================= */
  const handleMainFilter = useCallback((cat) => {
    setMainFilter(cat);
    setRealEstateFilter(""); // Main tab maarum podhu sub-filter reset aagum
  }, []);

  const handleSubFilter = useCallback((sub) => {
    setRealEstateFilter(sub); // Specific sub-topic sets active toggle
  }, []);

  return (
    <main className="portfolio-page">
      {/* HERO SECTION CONTAINER */}
      <section className="portfolio-hero">
        <div className="portfolio-hero-bg"></div>
        <div className="portfolio-header-content">
          <h1>Our Works</h1>
          <p>
            Explore Interior, Exterior, Drone and Virtual Editing Projects.
          </p>
        </div>
      </section>

      {/* MAIN TOPIC NAVIGATION */}
      <div className="main-filter-wrapper">
        {["Real Estate", "Graphic Design", "Photography", "Videography"].map(
          (cat) => (
            <button
              key={cat}
              onClick={() => handleMainFilter(cat)}
              className={mainFilter === cat ? "active-main-filter" : ""}
            >
              {cat}
            </button>
          )
        )}
      </div>

         
      {mainFilter === "Real Estate" && (
        <div className="sub-filter-wrapper">
          {[
            "Interior",
            "Exterior",
            "Drone",
            "Virtual Renovation",
            "Virtual Staging",
          ].map((sub) => (
            <button
              key={sub}
              onClick={() => handleSubFilter(sub)}
              className={realEstateFilter === sub ? "active-sub-filter" : ""}
            >
              {sub}
            </button>
          ))}
        </div>
      )}

      {/* IMAGES DISPLAY GRID GRID */}
      <section className="comparison-gallery">
        {filteredProjects.length > 0 ? (
          filteredProjects.map((project) => (
            <PortfolioCard key={project.id} project={project} />
          ))
        ) : mainFilter === "Real Estate" && !realEstateFilter ? (
          <div className="select-prompt-message">
            Please select a subcategory (like Interior, Exterior) to view our works.
          </div>
        ) : (
          <div className="select-prompt-message">No projects found.</div>
        )}
      </section>
    </main>
  );
}

export default memo(Portfolio);